import { Cookies } from 'react-cookie';
import { PAGES } from '@/consts';
import { CommonPageProps } from '@/shared/lib/types';
import { getSiteSettings } from './site-settings';
import { getSocnets } from './socnets';
import { getStocks } from './stocks';

export const getCommonPageProps = async (
    page?: number | null,
    cookies?: Partial<{ [key: string]: string }>,
): Promise<CommonPageProps> => {
    const [siteSettings, stocksResponse] = await Promise.all([
        getSiteSettings({
            params: {
                keys: [
                    'contacts_default_document_oferta',
                    'contacts_default_phone',
                    'contacts_default_email',
                    'contacts_default_support_link',
                    'contacts_default_postal_сode',
                    'contacts_default_city',
                    'contacts_default_street',
                    'contacts_default_location',
                    'footer_default_form_text',
                ],
            },
        }),
        getStocks({
            params: {
                page: page || PAGES.MAIN,
            },
        }),
    ]);

    const address = [
        siteSettings.data?.contacts_default_postal_сode,
        siteSettings.data?.contacts_default_city,
        siteSettings.data?.contacts_default_street,
        siteSettings.data?.contacts_default_location,
    ]
        .filter(Boolean)
        .join(', ');

    return {
        meta: {
            baseTitle: '',
            description: '',
            ogImage: '/img/og-image.jpg',
        },
        cookies: cookies ? new Cookies(cookies).getAll() : null,
        breadcrumbs: [],

        telegramData: {
            useTemporaryContent: true,
            standard: {
                title: 'Станьте частью профессионального сообщества с нами.',
                description: 'Присоединяйтесь к нашему Telegram-каналу и будьте в курсе всех новостей и обновлений!',
                buttonText: 'Подписаться',
                buttonLink: 'https://t.me/oilgasforum_TNF',
                image: {
                    src: '/img/telegram-popup/tg-qr-code.png',
                    width: 1,
                    height: 1,
                    alt: 'Ссылка на телеграм канал',
                    title: 'Ссылка на телеграм канал',
                },
            },
            temporary: {
                title: 'TNF в вашем телефоне',
                description:
                    'Личный кабинет TNF доступен и в мобильном приложении. Скачайте его, чтобы пользоваться всеми возможностями форума.',
                stores: [
                    {
                        name: 'Google Play',
                        href: 'https://play.google.com/store/apps/details?id=com.mintrocket.tnf',
                        icon: 'google-play',
                    },
                    {
                        name: 'App Store',
                        href: 'https://apps.apple.com/us/app/tnf/id6779165022',
                        icon: 'app-store',
                    },
                    {
                        name: 'RuStore',
                        href: 'https://www.rustore.ru/catalog/app/com.mintrocket.tnf',
                        icon: 'rustore',
                    },
                ],
                image: null,
            },
        },

        informationData: {
            title: 'Важная информация для гостей и участников!',
            subtitle:
                '17 сентября с 13:00 до 16:00 доступ в павильон TNF EXPO и на уличную экспозицию будет временно ограничен в связи с проведением официального мероприятия.',
            description: `
                Для прохода к деловой программе Форума в здании Тюменского Технопарка воспользуйтесь системой фасттрека, расположенной вдоль павильона HR-саммита со стороны улицы Мельникайте. Маршрут организован в обход закрытой зоны.
                </br>
                </br>
                Приносим извинения за временные неудобства и просим заранее планировать свои передвижения на площадке.
            `,
            timeDuration: 5,
        },

        stocksData: {
            title: stocksResponse.data?.[0]?.title || '',
            description: stocksResponse.data?.[0]?.description || '',
            formDescription: stocksResponse.data?.[0]?.formDescription || '',
            timeDuration: stocksResponse.data?.[0]?.timeDuration || 14,
        },
        footerData: {
            formText: siteSettings.data?.footer_default_form_text || '',
            address: address || '',
            phone: siteSettings.data?.contacts_default_phone || '',
            email: siteSettings.data?.contacts_default_email || '',
            street: siteSettings.data?.contacts_default_street || '',
            city: siteSettings.data?.contacts_default_city || '',
            postalCode: siteSettings.data?.contacts_default_postal_сode || '',
            socialLinks: [
                {
                    icon: `<svg width="17" height="14" viewBox="0 0 17 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.9519 1.38517L14.3857 13.1038C14.1965 13.9347 13.6858 14.1363 12.9733 13.7453L9.06412 10.9531L7.17888 12.7127C6.97081 12.9144 6.79426 13.0854 6.39073 13.0854L6.66816 9.23015L13.9128 2.88818C14.228 2.61935 13.8434 2.46661 13.4273 2.73544L4.47397 8.1976L0.61521 7.03063C-0.223375 6.77402 -0.24229 6.21802 0.791755 5.827L15.8674 0.193762C16.5673 -0.0506295 17.1789 0.358727 16.9519 1.38517Z" fill="#082E74"/>
                    </svg>
                    `,
                    href: 'https://t.me/oilgasforum_TNF',
                    name: 'Telegram',
                    rel: ['nofollow'],
                },
                {
                    icon: `<svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3.09718 3.1875H1.23776C0.706375 3.1875 0.600098 3.45055 0.600098 3.74134C0.600098 4.2594 1.23011 6.83087 3.53589 10.2309C5.07223 12.5536 7.23774 13.8125 9.20769 13.8125C10.3903 13.8125 10.5366 13.5324 10.5366 13.052V11.2965C10.5366 10.7373 10.6488 10.6254 11.0229 10.6254C11.2992 10.6254 11.7711 10.7704 12.8755 11.8897C14.1364 13.2175 14.3438 13.8125 15.0529 13.8125H16.9132C17.4446 13.8125 17.7107 13.5324 17.5568 12.9804C17.3893 12.431 16.7874 11.6329 15.9882 10.6872C15.5545 10.1486 14.905 9.56787 14.7077 9.27708C14.4314 8.90397 14.5105 8.73845 14.7077 8.4065C14.7077 8.4065 16.9736 5.04766 17.2099 3.90687C17.3289 3.49261 17.2099 3.1875 16.6471 3.1875H14.7877C14.3149 3.1875 14.0964 3.45055 13.9782 3.74134C13.9782 3.74134 13.0328 6.16697 11.6929 7.74261C11.2593 8.19892 11.062 8.34476 10.8256 8.34476C10.7075 8.34476 10.5366 8.19892 10.5366 7.78466V3.90687C10.5366 3.40939 10.3997 3.1875 10.0052 3.1875H7.08214C6.78712 3.1875 6.60942 3.41834 6.60942 3.63755C6.60942 4.10908 7.27939 4.21824 7.34826 5.54513V8.42708C7.34826 9.05966 7.24028 9.17418 7.00307 9.17418C6.37306 9.17418 4.83927 6.73692 3.92954 3.94892C3.751 3.40671 3.57245 3.1875 3.09718 3.1875Z" fill="#082E74"/>
                        <defs>
                        <rect width="17" height="17" fill="white" transform="translate(0.600098)"/>
                        </defs>
                    </svg>
                    `,
                    href: 'https://vk.com/oilgasforum_tnf',
                    name: 'VK',
                },
                {
                    icon: ` <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.64504 14.2115C6.32018 14.2115 5.7045 14.0172 4.6343 13.2402C3.95737 14.1144 1.81375 14.7975 1.72027 13.6287C1.72027 12.7514 1.52686 12.01 1.30766 11.2006C1.04656 10.2035 0.75 9.09301 0.75 7.48398C0.75 3.64108 3.88968 0.75 7.60958 0.75C11.3327 0.75 14.25 3.78353 14.25 7.51959C14.2625 11.1979 11.3074 14.1919 7.64504 14.2115ZM7.69984 4.07166C5.88824 3.97778 4.47635 5.23716 4.16367 7.21203C3.90579 8.84696 4.36353 10.838 4.75357 10.9416C4.94053 10.9869 5.41116 10.6049 5.7045 10.3103C6.18955 10.6468 6.75438 10.849 7.34203 10.8963C9.21914 10.987 10.8231 9.55171 10.9491 7.66851C11.0225 5.78133 9.57721 4.1829 7.69984 4.0749L7.69984 4.07166Z" fill="white"/>
                            </svg>
                    `,
                    href: 'https://max.ru/oilgasforum_TNF',
                    name: 'max',
                },
            ],
            copyrightYear: new Date().getFullYear(),
            privacy: '/privacy/',
            agreement: '/agreement/',
            oferta:
                siteSettings.data?.contacts_default_document_oferta &&
                siteSettings.data?.contacts_default_document_oferta.src
                    ? {
                          src: siteSettings.data.contacts_default_document_oferta.src,
                          title: siteSettings.data.contacts_default_document_oferta.title,
                          alt: siteSettings.data.contacts_default_document_oferta.alt,
                      }
                    : {
                          src: '/docs/dogovor-oferta-tnf.pdf',
                          title: 'Лицензионный договор - оферта',
                          alt: '',
                      },
            support: siteSettings.data?.contacts_default_support_link || 'https://t.me/tnf_oilgasforum_bot',
        },
    };
};
